import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Card } from '@televet/kibble-ui/Card';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';

import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/guardrails/add')({
  component: AddGuardrail,
  beforeLoad: () => {
    return {
      pageTitle: 'Add Guardrail',
    };
  },
});

export interface GuardrailFormData {
  name: string;
  guardrailIdentifier: string;
  guardrailVersion: string;
}

export function GuardrailForm({
  onSubmit,
  onCancel,
  errors,
  register,
  handleSubmit,
}: {
  onSubmit: (data: GuardrailFormData) => void;
  onCancel: () => void;
  errors: any;
  register: any;
  handleSubmit: any;
}) {
  return (
    <Flex
      flex="1 1 auto"
      direction="column"
      align="stretch"
      h="100%"
    >
      <Flex flex="1 1 auto" justify="center" align="flex-start" pt={8}>
        <Card
          flex="1 1 auto"
          direction="column"
          borderColor="border.default"
          maxW="3xl"
        >
          <Box p={10}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align="stretch">
                <TextInput
                  label="Name"
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  placeholder="Enter guardrail name"
                  autoFocus
                  {...register('name', { required: 'Name is required' })}
                />

                <TextInput
                  label="Guardrail Identifier"
                  required
                  invalid={!!errors.guardrailIdentifier}
                  errorText={errors.guardrailIdentifier?.message}
                  placeholder="Enter guardrail identifier (e.g. abc123def456)"
                  {...register('guardrailIdentifier', {
                    required: 'Guardrail identifier is required',
                  })}
                />

                <TextInput
                  label="Guardrail Version"
                  required
                  invalid={!!errors.guardrailVersion}
                  errorText={errors.guardrailVersion?.message}
                  placeholder="Enter version (e.g. 1)"
                  {...register('guardrailVersion', {
                    required: 'Guardrail version is required',
                  })}
                />

                <Flex justify="flex-end">
                  <Flex gap={2}>
                    <Button variant="secondary" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button type="submit">Save</Button>
                  </Flex>
                </Flex>
              </VStack>
            </form>
          </Box>
        </Card>
      </Flex>
    </Flex>
  );
}

function AddGuardrail() {
  const navigate = useNavigate();

  const { mutate } = useMutation(
    trpc.guardrail.createOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Guardrail created successfully',
          description: data.message,
        });
        navigate({ to: '/guardrails' });
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to create guardrail';
        toaster.error({
          title: 'Failed to create guardrail',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GuardrailFormData>();

  const onSubmit = (data: GuardrailFormData) => {
    mutate({
      name: data.name,
      guardrailIdentifier: data.guardrailIdentifier,
      guardrailVersion: data.guardrailVersion,
    });
  };

  const onCancel = () => {
    navigate({ to: '/guardrails' });
  };

  return (
    <GuardrailForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
    />
  );
}
